import { isSodimacStandalone } from '@digital-retail/store-manager';
import PropTypes from 'prop-types';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { noop } from '../../../../../../utils/noop';
import { YoutubeVideoStyles } from './YoutubeVideo.style';
import {
  cleanupTouchGuards,
  extractVideoId,
  formatYoutubeShortsVideoUrl,
  getYoutubeParams,
  initYoutubePlayer,
  logError,
  setupTouchGuards,
  stopVideo,
} from './helpers';

const YoutubeVideo = forwardRef(({ url, setVideoPlaying, isMobile = false, store }, ref) => {
  const formattedUrl = useMemo(() => formatYoutubeShortsVideoUrl(url), [url]);
  const containerRef = useRef(null);
  const iframeRef = useRef(null);

  const handleStopVideo = useCallback(() => {
    stopVideo(iframeRef.current);
    setVideoPlaying(false);
  }, [setVideoPlaying]);

  useImperativeHandle(
    ref,
    () => ({
      stopVideo: handleStopVideo,
    }),
    [handleStopVideo]
  );

  useEffect(() => {
    setVideoPlaying(true);

    if (iframeRef.current) {
      try {
        initYoutubePlayer(iframeRef.current, setVideoPlaying);
      } catch (error) {
        logError('Error starting YouTube player:', error);
      }
    }

    const currentContainer = containerRef.current;
    if (isMobile && currentContainer) {
      setupTouchGuards(currentContainer);
    }

    return () => {
      setVideoPlaying(false);
      if (isMobile && currentContainer) {
        cleanupTouchGuards(currentContainer);
      }
    };
  }, [url, setVideoPlaying, isMobile]);

  const videoId = useMemo(() => extractVideoId(formattedUrl), [formattedUrl]);
  const youtubeParams = useMemo(() => getYoutubeParams(isMobile, videoId, store), [isMobile, videoId, store]);
  const videoClassName = isMobile ? 'youtube-video-mobile' : 'youtube-video';
  const disablePointer = !isSodimacStandalone(store) ? 'pointer-events-none' : '';
  const iframeSrc = useMemo(() => `${formattedUrl}${youtubeParams}`, [formattedUrl, youtubeParams]);

  return (
    <>
      <div className={`${videoClassName} ${disablePointer}`.trim()} ref={containerRef}>
        <iframe
          ref={iframeRef}
          title="Video"
          src={iframeSrc}
          frameBorder="0"
          allowFullScreen
          allow="autoplay; fullscreen"
          loading="lazy"
        />
      </div>
      <style jsx>{YoutubeVideoStyles}</style>
    </>
  );
});

YoutubeVideo.displayName = 'YoutubeVideo';

YoutubeVideo.defaultProps = {
  url: '',
  setVideoPlaying: noop,
  isMobile: false,
  store: '',
};

YoutubeVideo.propTypes = {
  url: PropTypes.string,
  setVideoPlaying: PropTypes.func,
  isMobile: PropTypes.bool,
  store: PropTypes.string,
};

export default YoutubeVideo;
export { YoutubeVideo };
